import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, jobTime } from "@/components/status";
import { Button } from "@/components/ui/button";
import { MapPin, Calendar, Clock, Check, Store, Loader2, Gavel } from "lucide-react";
import { toast } from "sonner";

export default function Marketplace() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/jobs/marketplace").then((r) => setJobs(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const notReady = !user.stripe_onboarded || user.background_check_status !== "approved";

  const accept = async (id) => {
    setBusy(id);
    try {
      await api.post(`/jobs/${id}/accept`);
      toast.success("Job accepted!");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="marketplace">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Marketplace</h1>
        <p className="text-muted-foreground text-sm mt-1">Open jobs available to claim.</p>
      </div>

      {notReady && (
        <div className="border border-signal/40 bg-signal/5 rounded-md p-3 text-sm text-sapphire">
          Complete <button className="text-ocean font-semibold" onClick={() => navigate("/cleaner/onboarding")}>verification & Stripe onboarding</button> to accept jobs.
        </div>
      )}

      {jobs.length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">
          <Store className="w-8 h-8 mx-auto mb-3 opacity-40" /> No open jobs right now. Check back soon.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {jobs.map((j) => (
            <div key={j.id} className="border border-border rounded-md bg-white overflow-hidden" data-testid={`market-job-${j.id}`}>
              {j.property?.photos?.[0] && <img src={j.property.photos[0]} alt="" className="w-full aspect-[16/10] object-cover" />}
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <h3 className="font-heading font-bold text-lg text-sapphire">{j.property?.name}</h3>
                  <span className="font-heading font-black text-ocean" data-testid={`price-${j.id}`}>
                    {j.pricing_mode === "bidding" ? (j.budget_amount > 0 ? `Budget ${money(j.budget_amount)}` : "Offers") : money(j.payout_amount)}
                  </span>
                </div>
                {j.pricing_mode === "bidding" && (
                  <div className="text-xs mt-1 flex items-center gap-1 text-amber-700" data-testid={`bid-info-${j.id}`}>
                    <Gavel className="w-3 h-3" /> {j.bid_count} offer{j.bid_count === 1 ? "" : "s"}{j.my_bid ? ` · yours: ${money(j.my_bid.amount)}` : ""}
                  </div>
                )}
                <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" />{j.property?.address}</div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{j.date}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{jobTime(j)}</span>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" className="flex-1" onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`view-${j.id}`}>Details</Button>
                  {j.pricing_mode === "bidding" ? (
                    <Button className="flex-1 bg-ocean hover:bg-ocean/90" disabled={notReady} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`bid-${j.id}`}>
                      <Gavel className="w-4 h-4 mr-1" /> {j.my_bid ? "Edit Offer" : "Make Offer"}
                    </Button>
                  ) : (
                  <Button className="flex-1 bg-ocean hover:bg-ocean/90" disabled={notReady || busy === j.id} onClick={() => accept(j.id)} data-testid={`accept-${j.id}`}>
                    {busy === j.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Accept</>}
                  </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
